import React, { FC, MutableRefObject, ReactElement, useContext, useMemo, useState } from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import { Skeleton } from '@material-ui/lab'
import { SelectOptions } from '../../../../React/Common/Components/Selects'
import { LanguageContext, ReactState } from '../../../../React/Common/Contexts'
import { AdminFilters, AdminFilterValues } from '../../Molecules/AdminFilters/AdminFilters'
import { AdminActionsLanguage, GenericAdminFilterLanguage } from '../../../../../i18n'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { OutlinedTextInput } from '../../Atoms/Inputs/OutlinedTextInput'
import { ON_CHANGE } from '../../../../React/Common/Constants/HookFormEventNames'
import { FormProvider, useForm, UseFormMethods } from '../../../../Vendor/react-hook-form/dist/index.ie11'
import FormRow from '../../../../React/Common/Components/FormRow'
import { OutlinedSelect } from '../../Atoms/Inputs/OutlinedSelect'
import { RoundedButton } from '../../Atoms/RoundedButton/RoundedButton'
import { RouterCheckbox } from '../../Atoms/Inputs/RouterCheckbox'
import { IsVarEmpty } from '../../../../NonReact/utils/GlobalFunctions'
import { ANY_VALUE_NUMBER_NUMERIC } from '../../../../NonReact/common/DefaultValues'
import { AdminAction, ActionField, AdminControlFields } from './Types'
import { adminControlRequestAsync, AdminControlResponse } from '../../../../React/Common/API/AdminControlClient'
import { displayError } from '../../../../React/Common/Utils/Messages'
import { getActions } from './Actions'
import { PageTitle } from '../../Molecules/PageTitle/PageTitle'

export const AdminControl: FC = (): ReactElement => {
    const [filters, setFilters]: ReactState<Nullable<AdminFilterValues>> = useState()
    const [isLoading, setIsLoading]: ReactState<boolean> = useState(false)
    const [actionResult, setActionResult]: ReactState<Definable<string>> = useState()

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(AdminActionsLanguage.NAME)
    language.loadBundle(GenericAdminFilterLanguage.NAME)

    const actions: Record<AdminAction, ActionField[]> = getActions(language)
    const actionOptions = useMemo(
        (): SelectOptions<string> =>
            Object.keys(actions).map((action: string) => ({
                value: action,
                label: language.get(`Action_Label_${action}`)
            })),
        [actions]
    )

    const methods: UseFormMethods<AdminControlFields> = useForm<AdminControlFields>({
        defaultValues: { Action: AdminAction.MakeCall },
        mode: ON_CHANGE,
        reValidateMode: ON_CHANGE,
        shouldUnregister: false
    })

    const { watch, handleSubmit, reset }: UseFormMethods<AdminControlFields> = methods
    const selectedAction: AdminAction = watch('Action')

    const isPartitionNotSet: boolean =
        IsVarEmpty(filters?.partition?.value) || filters?.partition?.value === ANY_VALUE_NUMBER_NUMERIC
    const isFormItemDisabled: boolean = isPartitionNotSet || isLoading

    const createInput = (field: ActionField): ReactElement => {
        if (field.type === 'string' || field.type === 'password') {
            return (
                <OutlinedTextInput
                    type={field.type === 'password' ? 'password' : 'text'}
                    inputName={field.id}
                    disabled={isFormItemDisabled}
                    labelLeft={field.label}
                />
            )
        }

        if (field.type === 'list') {
            return <OutlinedSelect label={field.label} options={field.options ?? []} disabled={isFormItemDisabled} />
        }

        if (field.type === 'boolean') {
            return <RouterCheckbox labelLeft={field.label} name={field.id} disabled={isFormItemDisabled} />
        }

        return <></>
    }

    const executeAction = ({ action, ...data }: AdminControlFields): void => {
        setIsLoading(true)
        adminControlRequestAsync({
            Action: action,
            PartitionId: filters?.partition?.value as number,
            TelsysId: filters?.telephonyServer?.value as number,
            Data: {
                ...data
            }
        })
            .then((result: Nullable<AdminControlResponse>): void => {
                if (!IsVarEmpty(result) && isMounted.current) {
                    setActionResult(result.message)
                }
            })
            .catch(displayError)
            .finally((): void => {
                if (isMounted.current) setIsLoading(false)
            })
    }

    const renderActionResult = (): ReactElement => {
        if (isLoading) {
            return (
                <div className='admin-controls-wrapper'>
                    <Skeleton />
                    <Skeleton />
                    <Skeleton />
                </div>
            )
        }

        return actionResult ? <div className='admin-controls-wrapper'>{actionResult}</div> : <></>
    }

    return (
        <div className='admin-controls-container'>
            <PageTitle title={AdminActionsLanguage.Page_Title} />
            <div className='admin-controls-wrapper'>
                <AdminFilters
                    labelTelSysPartitionAllSentinelAsNone
                    optionsToDisplay={{
                        cme: true,
                        telephonyServer: true,
                        partition: true,
                        crmType: false,
                        scope: false,
                        codeType: false,
                        userType: false,
                        includeCmeChildren: false,
                        search: false,
                        includeRefresh: false
                    }}
                    localeText={{
                        cmeLabel: language.get(AdminActionsLanguage.Field_Label_CME),
                        telephonyServerLabel: language.get(AdminActionsLanguage.Field_Label_TelephonyServer),
                        partitionLabel: language.get(AdminActionsLanguage.Field_Label_Partition)
                    }}
                    onChange={(values: AdminFilterValues): void => setFilters(values)}
                    alignGrid={false}
                />
                <FormProvider {...methods}>
                    <form
                        // eslint-disable-next-line @typescript-eslint/no-misused-promises
                        onSubmit={handleSubmit(executeAction)}
                        noValidate
                    >
                        <FormRow>
                            <OutlinedSelect
                                name='Action'
                                label='Action'
                                options={actionOptions}
                                disabled={isFormItemDisabled}
                            />
                        </FormRow>
                        {actions?.[selectedAction]?.map((field: ActionField) => (
                            <FormRow key={field.id}>{createInput(field)}</FormRow>
                        ))}
                        <div className='admin-controls-action-buttons'>
                            <RoundedButton
                                type='button'
                                disabled={isFormItemDisabled}
                                onClick={(): void => reset()}
                                isOutlined={true}
                                shrink
                            >
                                {language.get(AdminActionsLanguage.Action_Button_Label_Reset)}
                            </RoundedButton>
                            <RoundedButton type='submit' disabled={isFormItemDisabled} shrink>
                                {language.get(AdminActionsLanguage.Action_Button_Label_Execute)}
                            </RoundedButton>
                        </div>
                    </form>
                </FormProvider>
            </div>
            {renderActionResult()}
        </div>
    )
}
